import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { useInstitution } from '@/contexts/InstitutionContext'
import { fetchCampaigns, type Campaign } from '@/lib/campaigns'
import { getMe, updateLastSelectedCampaign } from '@/lib/api/auth.service'
import { campaignToSelectOption } from '@/lib/institution-campaign-select'
import { canListInstitutionCampaigns } from '@/lib/collaborator-permissions'
import { ApiClientError } from '@/lib/api/client'

/**
 * Carrega campanhas, aplica a preferência salva no servidor (GET /me e coluna em instituição)
 * e persiste alterações (PUT /api/institution/last-selected-campaign).
 */
interface UseInstitutionCampaignSelectOptions {
  enabled?: boolean
}

export function useInstitutionCampaignSelect({
  enabled = true,
}: UseInstitutionCampaignSelectOptions = {}) {
  const { guard, token, institution, refreshMe, isCollaborator, hasPermission } = useAuth()
  const { institutionId } = useInstitution()
  const canListCampaigns = canListInstitutionCampaigns(Boolean(isCollaborator), hasPermission)

  const isAdminWithoutInstitution = guard === 'admin' && institutionId === null

  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  const [campaignsLoading, setCampaignsLoading] = useState(true)
  const [campaignsError, setCampaignsError] = useState<string | null>(null)
  const [campaignValue, setCampaignValue] = useState('')
  const [persistError, setPersistError] = useState<string | null>(null)
  const [preferredLoaded, setPreferredLoaded] = useState(
    () => guard !== 'admin' || institutionId === null
  )
  const [preferredId, setPreferredId] = useState<number | null>(() =>
    guard === 'institution' ? (institution?.last_selected_campaign_id ?? null) : null
  )

  const lastSeenPreferredRef = useRef<number | null | undefined>(undefined)
  const valueBeforeActionRef = useRef<string>('')
  const persistGenerationRef = useRef(0)

  useEffect(() => {
    if (guard === 'institution') {
      setPreferredLoaded(true)
      setPreferredId(institution?.last_selected_campaign_id ?? null)
      return
    }
    if (guard === 'admin' && institutionId) {
      let cancelled = false
      setPreferredLoaded(false)
      void getMe()
        .then((m) => {
          if (!cancelled) {
            setPreferredId(m.last_selected_campaign_id ?? null)
            setPreferredLoaded(true)
          }
        })
        .catch(() => {
          if (!cancelled) {
            setPreferredId(null)
            setPreferredLoaded(true)
          }
        })
      return () => {
        cancelled = true
      }
    }
    setPreferredId(null)
    setPreferredLoaded(true)
  }, [guard, institutionId, token, institution?.last_selected_campaign_id])

  const loadCampaigns = useCallback(async () => {
    if (!enabled || !canListCampaigns) {
      setCampaigns([])
      setCampaignsLoading(false)
      setCampaignsError(null)
      return
    }

    setCampaignsLoading(true)
    setCampaignsError(null)
    if (isAdminWithoutInstitution) {
      setCampaigns([])
      setCampaignsLoading(false)
      return
    }
    try {
      const result = await fetchCampaigns(token ?? undefined)
      setCampaigns(result)
    } catch {
      setCampaigns([])
      setCampaignsError('Não foi possível carregar as campanhas.')
    } finally {
      setCampaignsLoading(false)
    }
  }, [canListCampaigns, enabled, isAdminWithoutInstitution, token])

  useEffect(() => {
    void loadCampaigns()
  }, [loadCampaigns])

  const campaignOptions = useMemo(
    () => campaigns.filter((c) => !c.hidden_from_main).map(campaignToSelectOption),
    [campaigns]
  )

  useEffect(() => {
    valueBeforeActionRef.current = campaignValue
  }, [campaignValue])

  useEffect(() => {
    if (!preferredLoaded || campaignsLoading) {
      return
    }
    if (campaignOptions.length === 0) {
      setCampaignValue('')
      lastSeenPreferredRef.current = undefined
      return
    }

    if (lastSeenPreferredRef.current !== preferredId) {
      lastSeenPreferredRef.current = preferredId
      if (
        preferredId != null &&
        campaignOptions.some((o) => o.value === String(preferredId))
      ) {
        setCampaignValue(String(preferredId))
        return
      }
    }

    setCampaignValue((prev) => {
      if (prev && campaignOptions.some((o) => o.value === prev)) {
        return prev
      }
      if (
        preferredId != null &&
        campaignOptions.some((o) => o.value === String(preferredId))
      ) {
        return String(preferredId)
      }
      return campaignOptions[0].value
    })
  }, [preferredLoaded, preferredId, campaignsLoading, campaignOptions])

  const handleCampaignValueChange = useCallback(
    (value: string) => {
      const previous = valueBeforeActionRef.current
      setCampaignValue(value)
      setPersistError(null)
      const n = Number.parseInt(value, 10)
      if (!Number.isFinite(n) || n <= 0) {
        return
      }
      if (isCollaborator) {
        setPreferredId(n)
        lastSeenPreferredRef.current = n
        return
      }
      const gen = ++persistGenerationRef.current
      void (async () => {
        try {
          await updateLastSelectedCampaign(n)
        } catch (e) {
          if (gen !== persistGenerationRef.current) {
            return
          }
          setCampaignValue(previous)
          const msg =
            e instanceof ApiClientError
              ? e.message
              : e instanceof Error
                ? e.message
                : 'Não foi possível salvar a campanha selecionada.'
          setPersistError(msg)
          return
        }
        if (gen !== persistGenerationRef.current) {
          return
        }
        setPreferredId(n)
        lastSeenPreferredRef.current = n
        if (guard === 'institution') {
          try {
            await refreshMe()
          } catch {
            // Preferência já persistida no servidor; contexto local pode atualizar no próximo /me
          }
        }
      })()
    },
    [refreshMe, guard, isCollaborator]
  )

  const selectPlaceholder = isAdminWithoutInstitution
    ? 'Selecione uma instituição acima'
    : campaignsLoading
      ? 'Carregando campanhas...'
      : campaignOptions.length === 0
        ? 'Nenhuma campanha cadastrada'
        : guard === 'admin' && institutionId && !preferredLoaded
          ? 'Carregando preferência...'
          : 'Selecione uma campanha'

  const isSelectorDisabled =
    campaignsLoading ||
    campaignOptions.length === 0 ||
    isAdminWithoutInstitution ||
    (guard === 'admin' && institutionId !== null && !preferredLoaded)

  const campaignId = useMemo((): number | null => {
    if (!campaignValue) {
      return null
    }
    const parsed = Number.parseInt(campaignValue, 10)
    return Number.isFinite(parsed) ? parsed : null
  }, [campaignValue])

  const selectedCampaign = useMemo(
    () => campaigns.find((c) => String(c.id) === campaignValue) ?? null,
    [campaigns, campaignValue]
  )

  return {
    campaigns,
    campaignOptions,
    campaignValue,
    handleCampaignValueChange,
    selectPlaceholder,
    campaignsLoading,
    campaignsError,
    persistError,
    isSelectorDisabled,
    isAdminWithoutInstitution,
    campaignId,
    selectedCampaign,
  }
}
